import { useEffect, useRef, useState } from "react";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import { toast } from "react-toastify";
import { usePanel } from "hooks/usePanel";
import { GenericLoading } from "components/ui/Loader";
import { OperationResponse } from "api/actor";
import {
  applyValidationErrorsToForm,
  createPersonFieldResolver,
  parseApiError,
} from "shared/utils/apiValidationErrors";
import { pimFormConfig } from "./pimFormConfig";
import { PimFormValues } from "./PimFormValues";
import { PimLayout } from "./PimLayout";

interface PimFormProps {
  onSuccess?: () => void;
  onSubmit: (values: PimFormValues) => Promise<OperationResponse | undefined>;
}

export const PimForm: React.FC<PimFormProps> = ({ onSubmit, onSuccess }) => {
  const formikRef = useRef<FormikProps<PimFormValues>>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { setPanelActions, closePanel } = usePanel();

  useEffect(() => {
    if (formikRef.current) {
      setPanelActions({
        closeLabel: "COMMON.CANCEL",
        submitLabel: "COMMON.VALIDATE",
        submitDisabled: !isFormValid,
        onSubmit: () => {
          formikRef.current?.submitForm();
        },
      });
    }
  }, [formikRef, setPanelActions, isFormValid]);

  const handleSubmit = async (
    values: PimFormValues,
    helpers: FormikHelpers<PimFormValues>
  ) => {
    setIsLoading(true);
    try {
      const response = await onSubmit(values);
      if (response?.isSuccess) {
        onSuccess?.();
        closePanel();
      } else if (response?.errorMessage) {
        toast.error(response.errorMessage);
      }
    } catch (error) {
      console.error('Error on submit PIM:', error);
      const parsed = await parseApiError(error);
      const matched = applyValidationErrorsToForm({
        validationErrors: parsed.validationErrors,
        setFieldError: helpers.setFieldError,
        setFieldTouched: helpers.setFieldTouched,
        resolveField: createPersonFieldResolver(),
      });
      if (parsed.message || matched > 0) {
        toast.error(parsed.message || 'Veuillez corriger les champs en erreur');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative">
      {isLoading && <GenericLoading loading isParentSized />}
      <Formik
        enableReinitialize
        initialValues={{ ...pimFormConfig.initialValues, mandants: [] }}
        validationSchema={pimFormConfig.validationSchema}
        onSubmit={handleSubmit}
        innerRef={formikRef}
      >
        {({ isValid }) => {
          if (isValid !== isFormValid) {
            setIsFormValid(isValid);
          }
          return (
            <Form>
              <PimLayout />
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
